import { useNavigate } from 'react-router-dom';
import { businesses } from '@/mocks/businesses';

function FeaturedBusinessesSection() {
  const navigate = useNavigate();

  const featured = businesses.filter((b) => b.isVerified).slice(0, 6);

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <i
            key={i}
            className={`${
              i < fullStars
                ? 'ri-star-fill text-amber-400'
                : i === fullStars && hasHalf
                ? 'ri-star-half-fill text-amber-400'
                : 'ri-star-line text-foreground-400'
            } text-sm`}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-12 md:mb-16">
          <div>
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              En vedette
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50">
              Entreprises vérifiées
            </h2>
          </div>
          <button
            onClick={() => navigate('/directory')}
            className="flex items-center gap-2 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
          >
            Voir toutes les entreprises
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {featured.map((business) => (
            <button
              key={business.id}
              onClick={() => navigate(`/business/${business.slug}`)}
              className="group text-left rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 overflow-hidden transition-all duration-300"
            >
              {/* Cover */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={business.coverUrl}
                  alt={business.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent" />
                {business.isVerified && (
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-primary-500/90 text-foreground-950 text-xs font-semibold">
                    <i className="ri-verified-badge-line" />
                    Vérifié
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4 md:p-5">
                <div className="flex items-start gap-3 mb-3">
                  <img
                    src={business.logoUrl}
                    alt={business.name}
                    className="w-10 h-10 rounded-lg object-cover shrink-0"
                  />
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-base text-foreground-50 truncate">
                      {business.name}
                    </h3>
                    <p className="text-xs text-foreground-400 truncate">
                      {business.categoryName} &middot; {business.city}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  {renderStars(business.rating)}
                  <span className="text-xs text-foreground-400">
                    {business.rating} ({business.reviewCount} avis)
                  </span>
                </div>

                <p className="text-sm text-foreground-400 line-clamp-2 leading-relaxed">
                  {business.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeaturedBusinessesSection;